import React, { useEffect, useMemo, useState } from 'react';
import { FaDownload } from 'react-icons/fa';
import type { OperationOrder } from './OrderOperationsPanel';
import { downloadExcelSheet } from '../utils/downloadExcel';
import { sortNewestFirst } from '../utils/newestFirst';
import { useMarketingSpendReport } from '../hooks/useMarketingSpendReport';
import { issueToExcelRow, useIssueReports } from '../hooks/useIssueReports';
import { useOrdersReport } from '../hooks/useOrdersReport';
import { useWeeklySettlementReport } from '../hooks/useWeeklySettlementReport';
import {
  addDays,
  getRangeBounds,
  ordersInRange,
  startOfDay,
  toLocalYmd,
  type ReportsTimeRange,
} from '../utils/reportRange';
import { getOrderStatusDisplay } from '../utils/orderStatusLabel';
import {
  ammanYmd,
  cancellationRefund,
  orderHistoryRow,
  paymentDetailRow,
} from '../utils/reportExport';

function downloadExcel(
  filename: string,
  sheetName: string,
  rows: Record<string, string | number>[]
) {
  downloadExcelSheet(filename, sheetName, rows);
}

function formatOrderItemsSummary(order: OperationOrder): string {
  return order.items
    .map((i) => `${i.quantity}× ${i.name || i.menu_item_id}`)
    .join('; ');
}

interface BusinessReportsPanelProps {
  cafes: { id: string; name: string }[];
  selectedCafe?: string;
  isSupportAdmin?: boolean;
}

type ExportItem = {
  id: string;
  title: string;
  subtitle: string;
  badge?: string;
  template?: boolean;
  disabled?: boolean;
  onDownload: () => void;
};

function ExportRow({ item }: { item: ExportItem }) {
  return (
    <li
      id={`reports-export-${item.id}`}
      className="flex items-center gap-3 px-4 py-3 hover:bg-[#faf8f5] transition scroll-mt-24"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">{item.title}</p>
        <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
      </div>
      {item.badge != null && item.badge !== '' && (
        <span className="shrink-0 text-xs tabular-nums text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
          {item.badge}
        </span>
      )}
      {item.template && (
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">Template</span>
      )}
      <button
        type="button"
        onClick={item.onDownload}
        disabled={item.disabled}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#3d2918] text-[#fff8f2] text-xs font-semibold hover:bg-[#2a1d14] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <FaDownload className="text-[10px]" />
        Excel
      </button>
    </li>
  );
}

export default function BusinessReportsPanel({
  cafes,
  selectedCafe = '',
  isSupportAdmin = false,
}: BusinessReportsPanelProps) {
  const [timeRange, setTimeRange] = useState<ReportsTimeRange>('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  useEffect(() => {
    if (timeRange !== 'custom') return;
    const now = new Date();
    const todayStr = toLocalYmd(now);
    const sevenDaysAgo = toLocalYmd(addDays(startOfDay(now), -6));
    if (!customStart) setCustomStart(sevenDaysAgo);
    if (!customEnd) setCustomEnd(todayStr);
  }, [timeRange, customStart, customEnd]);

  const { start, end } = useMemo(
    () => getRangeBounds(timeRange, customStart, customEnd),
    [timeRange, customStart, customEnd]
  );
  const marketing = useMarketingSpendReport(selectedCafe, start, end);
  const weeklySettlement = useWeeklySettlementReport(selectedCafe);
  const ordersReport = useOrdersReport(selectedCafe, start, end);

  const issueReports = useIssueReports(selectedCafe, start, end, isSupportAdmin);

  const cafeNameById = useMemo(() => {
    const m: Record<string, string> = {};
    cafes.forEach((c) => {
      m[c.id] = c.name;
    });
    return m;
  }, [cafes]);

  const rangeOrders = useMemo(() => {
    return ordersInRange(ordersReport.orders, start, end);
  }, [ordersReport.orders, start, end]);

  const orderErrorRows = useMemo(() => {
    return sortNewestFirst(
      rangeOrders.filter((o) => o.status === 'cancelled'),
      (o) => o.created_at
    ).map((o) => ({
        order_id: o.id,
        order_number: o.order_number || `ORD-${o.id.slice(0, 8).toUpperCase()}`,
        created_at_amman: paymentDetailRow(o, o.cafe_name || cafeNameById[o.cafe_id] || '').created_at_amman,
        cafe: o.cafe_name || cafeNameById[o.cafe_id] || o.cafe_id,
        customer_email: o.user_email,
        issue_type: 'cancellation',
        items_in_error: formatOrderItemsSummary(o),
        order_status: o.status,
        cancelled_from_stage: getOrderStatusDisplay(o).stageLabel || '',
        order_total_jod: Number(o.total_amount || 0),
        ...cancellationRefund(o),
      }));
  }, [rangeOrders, cafeNameById]);

  const reportDate = () => ammanYmd();

  const downloadOrderErrorsReport = () => {
    const rows =
      orderErrorRows.length > 0
        ? orderErrorRows
        : [{ note: 'No canceled orders in this period.' }];
    downloadExcel(`order-errors-${reportDate()}`, 'Order errors', rows);
  };

  const downloadPaymentDetails = () => {
    const completed = rangeOrders.filter((o) => o.status === 'completed');
    const sorted = sortNewestFirst(completed, (o) => o.created_at);
    const rows =
      sorted.length > 0
        ? sorted.map((o) =>
            paymentDetailRow(o, o.cafe_name || cafeNameById[o.cafe_id] || '')
          )
        : [{ note: 'No completed orders in this period.' }];
    downloadExcel(`payment-details-${reportDate()}`, 'Payment details', rows);
  };

  const downloadIssueReports = () => {
    const rows =
      issueReports.issues.length > 0
        ? issueReports.issues.map(issueToExcelRow)
        : [{ note: 'No issue reports in this period.' }];
    downloadExcel(`issue-reports-${reportDate()}`, 'Issue reports', rows);
  };

  const downloadOrderHistory = () => {
    const sorted = sortNewestFirst(rangeOrders, (o) => o.created_at);
    const rows =
      sorted.length > 0
        ? sorted.map((o) =>
            orderHistoryRow(
              o,
              o.cafe_name || cafeNameById[o.cafe_id] || '',
              formatOrderItemsSummary(o)
            )
          )
        : [{ note: 'No orders in this period.' }];
    downloadExcel(`order-history-${reportDate()}`, 'Order history', rows);
  };

  const rangeLabels: Record<ReportsTimeRange, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    week: 'Past week',
    '12w': '12 weeks',
    '12m': '12 months',
    custom: 'Custom',
  };

  const exports: ExportItem[] = [
    {
      id: 'weekly-settlement',
      title: 'Weekly café settlement',
      subtitle: weeklySettlement.subtitle,
      badge:
        weeklySettlement.loading || weeklySettlement.error
          ? undefined
          : `${weeklySettlement.netPayable.toFixed(2)} JOD net`,
      disabled: weeklySettlement.downloadDisabled,
      onDownload: weeklySettlement.downloadExcel,
    },
    {
      id: 'marketing-spend',
      title: 'Marketing spend',
      subtitle: marketing.subtitle,
      badge: marketing.loading || marketing.error ? undefined : `${marketing.totalCount}`,
      disabled: marketing.downloadDisabled,
      onDownload: marketing.downloadExcel,
    },
    ...(isSupportAdmin
      ? [
          {
            id: 'issue-reports',
            title: 'Issue reports',
            subtitle: issueReports.subtitle,
            badge:
              issueReports.loading || issueReports.error
                ? undefined
                : `${issueReports.count}`,
            disabled: issueReports.downloadDisabled,
            onDownload: downloadIssueReports,
          } as ExportItem,
        ]
      : []),
    {
      id: 'order-history',
      title: 'Order history',
      subtitle: ordersReport.loading
        ? 'Loading orders for this period…'
        : ordersReport.error
          ? ordersReport.error
          : ordersReport.truncated
            ? 'First 5,000 orders in the selected period with line items.'
            : 'All orders in the selected period with line items.',
      badge: ordersReport.loading ? '…' : `${rangeOrders.length}`,
      disabled: ordersReport.loading || Boolean(ordersReport.error),
      onDownload: downloadOrderHistory,
    },
    {
      id: 'order-errors',
      title: 'Order errors & inaccurate orders',
      subtitle: 'Canceled orders with items, refunds, and payout placeholders.',
      badge: ordersReport.loading ? '…' : `${orderErrorRows.length}`,
      disabled: ordersReport.loading || Boolean(ordersReport.error),
      onDownload: downloadOrderErrorsReport,
    },
    {
      id: 'payment-details',
      title: 'Payment details',
      subtitle: 'Completed orders: payment method, wallet, points, discounts, total.',
      badge: ordersReport.loading
        ? '…'
        : `${rangeOrders.filter((o) => o.status === 'completed').length}`,
      disabled: ordersReport.loading || Boolean(ordersReport.error),
      onDownload: downloadPaymentDetails,
    },
  ];

  return (
    <div className="space-y-5 max-w-4xl">
      <p className="text-sm text-gray-600">
        Download Excel reports below. Order, issue, and marketing exports load from the server
        for the period you pick (not only the Home queue). Marketing spend uses two sheets
        (campaigns, offers).
        {selectedCafe ? ' Filtered to the café selected in the sidebar.' : ''}
      </p>

      <section className="rounded-xl border border-[#e0d0c0] bg-[#fffcf7] overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-[#e0d0c0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-[#3d2918]">Reports</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Period: <span className="font-semibold text-gray-700">{rangeLabels[timeRange]}</span>
              {' · '}
              <button
                type="button"
                onClick={() => {
                  marketing.reload();
                  weeklySettlement.reload();
                  ordersReport.reload();
                  if (isSupportAdmin) issueReports.reload();
                }}
                disabled={
                  marketing.loading ||
                  weeklySettlement.loading ||
                  ordersReport.loading ||
                  issueReports.loading
                }
                className="text-gray-600 hover:text-[#3d2918] disabled:opacity-50"
              >
                Refresh data
              </button>
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(rangeLabels) as ReportsTimeRange[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  if (key === 'custom' && !customStart && !customEnd) {
                    const now = new Date();
                    setCustomStart(toLocalYmd(addDays(startOfDay(now), -6)));
                    setCustomEnd(toLocalYmd(now));
                  }
                  setTimeRange(key);
                }}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                  timeRange === key
                    ? 'bg-[#3d2918] text-[#fff8f2]'
                    : 'bg-white border border-[#e0d0c0] text-gray-600 hover:border-[#c9b8a4]'
                }`}
              >
                {rangeLabels[key]}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-3 bg-white items-end">
          <label className="text-xs text-gray-600">
            Settlement week (Monday)
            <input
              type="date"
              value={weeklySettlement.weekStart}
              onChange={(e) => weeklySettlement.setWeekStart(e.target.value)}
              className="mt-1 block rounded-md border border-gray-200 px-2 py-1.5 text-sm"
            />
          </label>
          <p className="text-[11px] text-gray-500 max-w-md pb-1">
            Leave as loaded default for last complete Mon–Sun (Jordan). Net bank transfer = card/digital
            gross minus all fees. Qahwahub credit & points subsidies are included in gross via platform.
            Cash at café is shown separately and not paid again.
          </p>
        </div>

        {timeRange === 'custom' && (
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-3 bg-white">
            <label className="text-xs text-gray-600">
              From
              <input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="mt-1 block rounded-md border border-gray-200 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs text-gray-600">
              To
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="mt-1 block rounded-md border border-gray-200 px-2 py-1.5 text-sm"
              />
            </label>
          </div>
        )}

        <ul className="divide-y divide-[#ebe3d6]">
          {exports.map((item) => (
            <ExportRow key={item.id} item={item} />
          ))}
        </ul>
      </section>
    </div>
  );
}
